import { PrimitiveType, } from "@buildwithflux/models";
import { Shape as ThreeShape } from "three";
import { JoinType } from "../../../clipper";
import { mat3 } from "../../../common/geometry/mat3";
import { vec2 } from "../../../common/geometry/vec2";
import { TraceCorners } from "../../libs/traceCorners";
import { booleanDifferencePolygons, booleanUnionPolygons, resizePolygonByExpansion } from "./utils";
/**
 * Given a primitive returns a set of (already transformed) points that fit the primitive border.
 * NOTE: The segments parameter is just a suggestion, the function can return less or more points when needed.
 */
export function approxFittingPolygon(primitive, segments, outside = false) {
    switch (primitive.type) {
        case PrimitiveType.Circle:
            return circleFittingPolygon(primitive, segments, outside);
        case PrimitiveType.Oblong:
            return oblongFittingPolygon(primitive, segments, outside);
        case PrimitiveType.Rectangle:
            return rectangleFittingPolygon(primitive, segments, outside);
        case PrimitiveType.RoundedRectangle:
            return roundedRectangleFittingPolygon(primitive, segments, outside);
        case PrimitiveType.Line:
            return lineFittingPolygon(primitive, segments, outside);
        case PrimitiveType.LineArc:
            return arcFittingPolygon(primitive, segments, outside);
        case PrimitiveType.ApproxPolygon:
            return approxPolygonFittingPolygon(primitive, segments, outside);
        case PrimitiveType.Expansion:
            return expansionFittingPolygon(primitive, segments, outside);
        case PrimitiveType.Union:
            return unionFittingPolygon(primitive, segments, outside);
        case PrimitiveType.Difference:
            return differenceFittingPolygon(primitive, segments, outside);
        default:
            return [];
    }
}
// NOTE: If you ask for less than 3 segments you will get incorrect results!!
function computeCircleErrorRadius(segments, radius) {
    if (segments < 3) {
        throw new Error("Cannot approximate circle with fewer than 3 segments");
    }
    // APPROXIMATION: To keep the polygon outside the circle itself we have to offset it by the error
    const segmentAngleDist = (Math.PI * 2) / segments;
    // https://www.geogebra.org/calculator/myajdn9h
    const r = radius;
    const l = segmentAngleDist;
    const segSq = (r - (r + Math.cos(l) * r) / 2) ** 2 + ((Math.sin(l) * r) / 2) ** 2;
    const dis = r - Math.sqrt(r ** 2 - segSq);
    const newRadius = r + dis / Math.cos(l / 2);
    return newRadius;
}
function circleFittingPolygon(primitive, segments, outside) {
    const newRadius = outside ? computeCircleErrorRadius(segments, primitive.radius) : primitive.radius;
    const points = new Array(segments).fill(0).map((_p, i) => {
        const angle = (i * Math.PI * 2) / segments;
        return { x: Math.cos(angle) * newRadius, y: Math.sin(angle) * newRadius };
    });
    return transformFittingPolygon(points, primitive.transform);
}
function arcFittingPolygon(primitive, segments, _outside) {
    const outerPoints = [];
    const innerPoints = [];
    const h = primitive.thickness / 2;
    for (let i = 0; i < segments; i++) {
        const { start, end } = TraceCorners.segmentByIndex(i, segments, primitive);
        const vector = vec2.sub(end, start);
        const normal = vec2.normalize({ x: -vector.y, y: vector.x });
        if (!normal) {
            continue;
        }
        const thickness = vec2.scale(normal, primitive.thickness / 2);
        const outer = vec2.add(start, thickness);
        const inner = vec2.sub(start, thickness);
        outerPoints.push({ x: outer.x, y: outer.y });
        innerPoints.unshift({ x: inner.x, y: inner.y });
    }
    const cap = (mid, n, sgn, slices = 6) => {
        const a0 = Math.atan2(n.y, n.x);
        const pts = [];
        for (let k = 1; k < slices; k++) {
            const a = a0 + (sgn * (Math.PI * k)) / slices;
            pts.push({ x: mid.x + Math.cos(a) * h, y: mid.y + Math.sin(a) * h });
        }
        return pts;
    };
    const { start: startStart, end: startEnd } = TraceCorners.segmentByIndex(0, segments, primitive);
    const p1 = startEnd;
    const vector1 = vec2.sub(startEnd, startStart);
    const n1 = vec2.normalize({ x: -vector1.y, y: vector1.x }) ?? { x: 0, y: 0 };
    const { start: endStart, end: endEnd } = TraceCorners.segmentByIndex(segments - 1, segments, primitive);
    const p0 = endEnd;
    const vector2 = vec2.sub(endEnd, endStart);
    const n0 = vec2.normalize({ x: -vector2.y, y: vector2.x }) ?? { x: 0, y: 0 };
    const polygon = [
        ...outerPoints.reverse(),
        ...cap(p1, n1, 1),
        ...innerPoints.reverse(),
        ...cap(p0, n0, -1).reverse(),
    ];
    return transformFittingPolygon(polygon, primitive.transform);
}
function oblongFittingPolygon(primitive, segments, outside) {
    if (primitive.width === primitive.height) {
        // Normal circle
        return circleFittingPolygon({ radius: primitive.width, transform: primitive.transform }, segments, outside);
    }
    const rectShort = Math.min(primitive.width, primitive.height);
    const rectLong = Math.max(primitive.width, primitive.height) - rectShort;
    const radius = rectShort / 2;
    const nSegmentsCap = Math.floor((segments - 4) / 2);
    const newRadius = outside ? computeCircleErrorRadius(segments - 4, radius) : radius;
    const rectShortWithError = newRadius * 2; // Needed to preserve convexity
    if (primitive.width > primitive.height) {
        const startAngle = Math.PI / 2 + Math.PI / nSegmentsCap / 2;
        const endAngle = (3 * Math.PI) / 2 + Math.PI / nSegmentsCap / 2;
        const angleRange = endAngle - startAngle;
        const capPoints = new Array(nSegmentsCap).fill(0).map((_p, i) => {
            const angle = startAngle + i * (angleRange / nSegmentsCap);
            return { x: Math.cos(angle) * newRadius, y: Math.sin(angle) * newRadius };
        });
        const leftCapPoints = capPoints.map((p) => ({ x: p.x - rectLong / 2, y: p.y })).reverse();
        const rightCapPoints = capPoints.map((p) => ({ x: rectLong / 2 - p.x, y: p.y }));
        return transformFittingPolygon([
            ...leftCapPoints,
            { x: -rectLong / 2, y: rectShortWithError / 2 },
            { x: rectLong / 2, y: rectShortWithError / 2 },
            ...rightCapPoints,
            { x: rectLong / 2, y: -rectShortWithError / 2 },
            { x: -rectLong / 2, y: -rectShortWithError / 2 },
        ], primitive.transform);
    }
    else {
        const startAngle = 0 + Math.PI / nSegmentsCap / 2;
        const endAngle = Math.PI + Math.PI / nSegmentsCap / 2;
        const angleRange = endAngle - startAngle;
        const capPoints = new Array(nSegmentsCap).fill(0).map((_p, i) => {
            const angle = startAngle + i * (angleRange / nSegmentsCap);
            return { x: Math.cos(angle) * newRadius, y: Math.sin(angle) * newRadius };
        });
        const topCapPoints = capPoints.map((p) => ({ x: p.x, y: rectLong / 2 + p.y }));
        const bottomCapPoints = capPoints.map((p) => ({ x: p.x, y: -p.y - rectLong / 2 })).reverse();
        return transformFittingPolygon([
            ...topCapPoints,
            { x: -rectShortWithError / 2, y: rectLong / 2 },
            { x: -rectShortWithError / 2, y: -rectLong / 2 },
            ...bottomCapPoints,
            { x: rectShortWithError / 2, y: -rectLong / 2 },
            { x: rectShortWithError / 2, y: rectLong / 2 },
        ], primitive.transform);
    }
}
function rectangleFittingPolygon(primitive, _segments, _outside) {
    const points = [
        { x: -primitive.width / 2, y: -primitive.height / 2 },
        { x: primitive.width / 2, y: -primitive.height / 2 },
        { x: primitive.width / 2, y: primitive.height / 2 },
        { x: -primitive.width / 2, y: primitive.height / 2 },
    ];
    return transformFittingPolygon(points, primitive.transform);
}
function roundedRectangleFittingPolygon(primitive, segments, _outside) {
    // TODO: Shape outside not supported for now
    // We use ThreeJS as shortcut, as we already have this code written
    const left = -primitive.width / 2;
    const bottom = -primitive.height / 2;
    const threeShape = new ThreeShape();
    threeShape.absarc(left + primitive.radiusBottomLeft, bottom + primitive.radiusBottomLeft, primitive.radiusBottomLeft, -Math.PI / 2, -Math.PI, true);
    threeShape.absarc(left + primitive.radiusTopLeft, bottom + primitive.height - primitive.radiusTopLeft, primitive.radiusTopLeft, Math.PI, Math.PI / 2, true);
    threeShape.absarc(left + primitive.width - primitive.radiusTopRight, bottom + primitive.height - primitive.radiusTopRight, primitive.radiusTopRight, Math.PI / 2, 0, true);
    threeShape.absarc(left + primitive.width - primitive.radiusBottomRight, bottom + primitive.radiusBottomRight, primitive.radiusBottomRight, 0, -Math.PI / 2, true);
    const points = threeShape.getPoints(segments);
    return transformFittingPolygon(points, primitive.transform);
}
function lineFittingPolygon(primitive, segments, outside) {
    // Treat it as a rotated oblong
    const pa = { x: primitive.startX, y: primitive.startY };
    const pb = { x: primitive.endX, y: primitive.endY };
    const len = vec2.distance(pa, pb);
    if (len === 0) {
        // To prevent divide by zero, a line with zero length is a circle
        return circleFittingPolygon({ radius: primitive.thickness / 2, transform: primitive.transform }, segments, outside);
    }
    const d = vec2
        .chain(pb)
        .sub(pa)
        .scale(1 / len)
        .xy();
    const center = vec2
        .chain(pa)
        .add(pb)
        .scale(1 / 2)
        .xy();
    const height = primitive.thickness;
    const width = len + height;
    const mat = mat3.transpose([d.x, -d.y, center.x, d.y, d.x, center.y, 0, 0, 1]);
    return transformFittingPolygon(oblongFittingPolygon({ width, height, transform: mat }, segments, outside), primitive.transform);
}
function approxPolygonFittingPolygon(primitive, _segments, _outside) {
    // TODO: here we are not really computing the fitting polygon but just returning the points as they are,
    // without limiting the points with the input segments nor offsetting the primitive approximation error,
    // since it's quite difficult to compute with an SVG/DXF source.
    // This approximation works for now as it's the same one that it's used as well for gerber and rendering,
    // but we will have to revisit it in the future.
    return transformFittingPolygon(primitive.points, primitive.transform);
}
function expansionFittingPolygon(primitive, segments, outside) {
    const origPolygon = approxFittingPolygon(primitive.primitive, segments, outside);
    // WARNING: could return undefined, the unexpanded polygon is used in that case
    const expandedPolygon = resizePolygonByExpansion(origPolygon, primitive.expansion, {
        joinType: JoinType.Round,
    }) ?? origPolygon;
    return transformFittingPolygon(expandedPolygon, primitive.transform);
}
function unionFittingPolygon(primitive, segments, outside) {
    const origPolygonA = approxFittingPolygon(primitive.primitiveA, segments, outside);
    const origPolygonB = approxFittingPolygon(primitive.primitiveB, segments, outside);
    const transformedPolygon = booleanUnionPolygons(origPolygonA, origPolygonB);
    return transformFittingPolygon(transformedPolygon ?? [], primitive.transform);
}
function differenceFittingPolygon(primitive, segments, outside) {
    const origPolygonA = approxFittingPolygon(primitive.primitiveA, segments, outside);
    const origPolygonB = approxFittingPolygon(primitive.primitiveB, segments, outside);
    const transformedPolygon = booleanDifferencePolygons(origPolygonA, origPolygonB);
    return transformFittingPolygon(transformedPolygon ?? [], primitive.transform);
}
function transformFittingPolygon(points, transform) {
    return points.map((op) => vec2.applyMatrix3(op, transform));
}
//# sourceMappingURL=approxFittingPolygon.js.map